import { useState, useEffect } from 'react';
import { User, Cpu, Users, Cloud } from 'lucide-react';
import { GoogleDriveAuth } from '../components/settings/GoogleDriveAuth';

const API_URL = import.meta.env.VITE_API_URL || 'https://heritage-os-api.uplaytv3117.workers.dev/api/v1';

export default function Settings() {
  const [activeTab, setActiveTab] = useState('profile');
  
  // AI Keys state
  const [geminiKey, setGeminiKey] = useState('');
  const [deepseekKey, setDeepseekKey] = useState('');
  const [nvidiaKey, setNvidiaKey] = useState('');
  const [groqKey, setGroqKey] = useState('');
  const [githubKey, setGithubKey] = useState('');
  const [youtubeKey, setYoutubeKey] = useState('');
  const [jwplayerKey, setJwplayerKey] = useState('');
  const [jwplayerUrl, setJwplayerUrl] = useState('');
  const [saveMessage, setSaveMessage] = useState('');

  // Profile State
  const [profileEmail, setProfileEmail] = useState('');
  const [profileName, setProfileName] = useState('');
  const [profilePassword, setProfilePassword] = useState('');
  const [profileMsg, setProfileMsg] = useState('');

  // Users State
  const [users, setUsers] = useState<any[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserRole, setNewUserRole] = useState('Editor');
  const [newUserName, setNewUserName] = useState('');
  const [userMsg, setUserMsg] = useState('');

  // Storage State
  const [gdriveAccounts, setGdriveAccounts] = useState<any[]>([]);
  const [loadingGdrive, setLoadingGdrive] = useState(false);
  const [newDriveEmail, setNewDriveEmail] = useState('');
  const [newDriveJson, setNewDriveJson] = useState('');
  const [driveMsg, setDriveMsg] = useState('');

  const token = localStorage.getItem('jwt_token') || '';
  let userRole = 'Editor';
  try {
    const userStr = localStorage.getItem('user_data');
    if (userStr) {
      const u = JSON.parse(userStr);
      if (u.role) userRole = u.role;
    } else if (token) {
      const payload = JSON.parse(atob(token.split('.')[1]));
      userRole = payload.role;
    }
  } catch (e) {
    // ignore
  }

  useEffect(() => {
    // Load profile from local storage if available
    const userStr = localStorage.getItem('user_info');
    if (userStr) {
      try {
        const u = JSON.parse(userStr);
        setProfileEmail(u.email || '');
        setProfileName(u.full_name || '');
      } catch(e) {}
    }

    // Load AI keys from backend
    fetch(`${API_URL}/settings/ai`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
    .then(r => r.json())
    .then(data => {
      setGeminiKey(data.gemini_api_key || '');
      setDeepseekKey(data.deepseek_api_key || '');
      setNvidiaKey(data.nvidia_api_key || '');
      setGroqKey(data.groq_api_key || '');
      setGithubKey(data.github_api_key || '');
      setYoutubeKey(data.youtube_api_key || '');
      setJwplayerKey(data.jwplayer_license_key || '');
      setJwplayerUrl(data.jwplayer_library_url || '');
      
      if (data.gemini_api_key) localStorage.setItem('GEMINI_API_KEY', data.gemini_api_key); else localStorage.removeItem('GEMINI_API_KEY');
      if (data.deepseek_api_key) localStorage.setItem('DEEPSEEK_API_KEY', data.deepseek_api_key); else localStorage.removeItem('DEEPSEEK_API_KEY');
      if (data.nvidia_api_key) localStorage.setItem('NVIDIA_NIM_API_KEY', data.nvidia_api_key); else localStorage.removeItem('NVIDIA_NIM_API_KEY');
      if (data.groq_api_key) localStorage.setItem('GROQ_API_KEY', data.groq_api_key); else localStorage.removeItem('GROQ_API_KEY');
      if (data.github_api_key) localStorage.setItem('GITHUB_API_KEY', data.github_api_key); else localStorage.removeItem('GITHUB_API_KEY');
      if (data.youtube_api_key) localStorage.setItem('YOUTUBE_API_KEY', data.youtube_api_key); else localStorage.removeItem('YOUTUBE_API_KEY');
      if (data.jwplayer_license_key) localStorage.setItem('JWPLAYER_LICENSE_KEY', data.jwplayer_license_key); else localStorage.removeItem('JWPLAYER_LICENSE_KEY');
      if (data.jwplayer_library_url) localStorage.setItem('JWPLAYER_LIBRARY_URL', data.jwplayer_library_url); else localStorage.removeItem('JWPLAYER_LIBRARY_URL');
    })
    .catch(console.error);

  }, []);

  useEffect(() => {
    if (activeTab === 'users' && userRole === 'admin') {
      fetchUsers();
    } else if (activeTab === 'storage' && userRole === 'admin') {
      fetchGdriveAccounts();
    }
  }, [activeTab]);

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const res = await fetch(`${API_URL}/auth/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setUsers(await res.json());
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleSaveAI = async (e: any) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_URL}/settings/ai`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          gemini_api_key: geminiKey,
          deepseek_api_key: deepseekKey,
          nvidia_api_key: nvidiaKey,
          groq_api_key: groqKey,
          github_api_key: githubKey,
          youtube_api_key: youtubeKey,
          jwplayer_license_key: jwplayerKey,
          jwplayer_library_url: jwplayerUrl
        })
      });
      if (res.ok) {
        setSaveMessage('Platform Keys saved securely in the cloud.');
        if (geminiKey) localStorage.setItem('GEMINI_API_KEY', geminiKey); else localStorage.removeItem('GEMINI_API_KEY');
        if (deepseekKey) localStorage.setItem('DEEPSEEK_API_KEY', deepseekKey); else localStorage.removeItem('DEEPSEEK_API_KEY');
        if (nvidiaKey) localStorage.setItem('NVIDIA_NIM_API_KEY', nvidiaKey); else localStorage.removeItem('NVIDIA_NIM_API_KEY');
        if (groqKey) localStorage.setItem('GROQ_API_KEY', groqKey); else localStorage.removeItem('GROQ_API_KEY');
        if (githubKey) localStorage.setItem('GITHUB_API_KEY', githubKey); else localStorage.removeItem('GITHUB_API_KEY');
        if (youtubeKey) localStorage.setItem('YOUTUBE_API_KEY', youtubeKey); else localStorage.removeItem('YOUTUBE_API_KEY');
        if (jwplayerKey) localStorage.setItem('JWPLAYER_LICENSE_KEY', jwplayerKey); else localStorage.removeItem('JWPLAYER_LICENSE_KEY');
        if (jwplayerUrl) localStorage.setItem('JWPLAYER_LIBRARY_URL', jwplayerUrl); else localStorage.removeItem('JWPLAYER_LIBRARY_URL');
      } else {
        setSaveMessage('Failed to save AI Keys.');
      }
    } catch (e) {
      setSaveMessage('Error saving AI Keys.');
    }
    setTimeout(() => setSaveMessage(''), 3000);
  };

  const handleSaveProfile = async (e: any) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_URL}/auth/profile`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: profileEmail,
          full_name: profileName,
          password: profilePassword || undefined
        })
      });
      const data = await res.json();
      if (res.ok) {
        setProfileMsg('Profile updated successfully.');
        setProfilePassword('');
        // Update local storage
        const userStr = localStorage.getItem('user_info');
        if (userStr) {
          const u = JSON.parse(userStr);
          u.email = profileEmail;
          u.full_name = profileName;
          localStorage.setItem('user_info', JSON.stringify(u));
        }
      } else {
        setProfileMsg(data.error || 'Failed to update profile');
      }
    } catch (err) {
      setProfileMsg('An error occurred');
    }
    setTimeout(() => setProfileMsg(''), 3000);
  };

  const handleCreateUser = async (e: any) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_URL}/auth/users`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: newUserEmail,
          password: newUserPassword,
          full_name: newUserName,
          role: newUserRole
        })
      });
      const data = await res.json();
      if (res.ok) {
        setUserMsg('User created successfully');
        setNewUserEmail(''); setNewUserPassword(''); setNewUserName('');
        fetchUsers();
      } else {
        setUserMsg(data.error || 'Failed to create user');
      }
    } catch (err) {
      setUserMsg('Error creating user');
    }
    setTimeout(() => setUserMsg(''), 3000);
  };

  const handleDeleteUser = async (uuid: string) => {
    if (!confirm('Are you sure you want to delete this user?')) return;
    try {
      const res = await fetch(`${API_URL}/auth/users/${uuid}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        fetchUsers();
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchGdriveAccounts = async () => {
    setLoadingGdrive(true);
    try {
      const res = await fetch(`${API_URL}/gdrive-accounts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setGdriveAccounts(await res.json());
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingGdrive(false);
    }
  };

  const handleAddDriveAccount = async (e: any) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_URL}/gdrive-accounts`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: newDriveEmail,
          credentials_json: newDriveJson
        })
      });
      const data = await res.json();
      if (res.ok) {
        setDriveMsg('Account added successfully');
        setNewDriveEmail('');
        setNewDriveJson('');
        fetchGdriveAccounts();
      } else {
        setDriveMsg(data.error || 'Failed to add account');
      }
    } catch (err) {
      setDriveMsg('Error adding account');
    }
    setTimeout(() => setDriveMsg(''), 3000);
  };

  const handleDeleteDriveAccount = async (id: string) => {
    if (!confirm('Are you sure you want to delete this Google Drive account?')) return;
    try {
      const res = await fetch(`${API_URL}/gdrive-accounts/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        fetchGdriveAccounts();
      }
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-8">
      <div className="flex flex-col items-start gap-2">
        <h1 className="text-2xl font-semibold text-slate-900">Settings</h1>
        <p className="text-slate-500 text-sm">
          Manage your account settings and preferences.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
        <div className="md:col-span-1 space-y-1">
          <button 
            onClick={() => setActiveTab('profile')}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'profile' ? 'bg-slate-100 text-slate-900' : 'text-slate-600 hover:bg-slate-50'}`}
          >
            <User size={18} />
            Profile
          </button>
          
          {userRole === 'admin' && (
            <>
              <button 
                onClick={() => setActiveTab('users')}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'users' ? 'bg-slate-100 text-slate-900' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                <Users size={18} />
                Users
              </button>
              
              <button 
                onClick={() => setActiveTab('storage')}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'storage' ? 'bg-slate-100 text-slate-900' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                <Cloud size={18} />
                Storage (GDrive)
              </button>
            </>
          )}

          <button 
            onClick={() => setActiveTab('ai')}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'ai' ? 'bg-slate-100 text-slate-900' : 'text-slate-600 hover:bg-slate-50'}`}
          >
            <Cpu size={18} />
            Integrations & API Keys
          </button>
        </div>

        <div className="md:col-span-3">
          {activeTab === 'profile' && (
            <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
              <h2 className="text-lg font-medium text-slate-900 mb-4">Profile Information</h2>
              <form className="space-y-4" onSubmit={handleSaveProfile}>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Full Name</label>
                  <input type="text" value={profileName} onChange={(e) => setProfileName(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md outline-none focus:ring-2 focus:ring-indigo-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                  <input type="email" value={profileEmail} onChange={(e) => setProfileEmail(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md outline-none focus:ring-2 focus:ring-indigo-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">New Password (leave blank to keep current)</label>
                  <input type="password" value={profilePassword} onChange={(e) => setProfilePassword(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md outline-none focus:ring-2 focus:ring-indigo-500" />
                </div>
                {profileMsg && <p className="text-sm text-green-600">{profileMsg}</p>}
                <button type="submit" className="px-4 py-2 bg-slate-900 text-white rounded-md hover:bg-slate-800 text-sm font-medium transition-colors">
                  Save Changes
                </button>
              </form>
            </div>
          )}

          {activeTab === 'users' && userRole === 'admin' && (
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                <h2 className="text-lg font-medium text-slate-900 mb-4">Add New User</h2>
                <form className="space-y-4" onSubmit={handleCreateUser}>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Full Name</label>
                      <input required type="text" value={newUserName} onChange={(e) => setNewUserName(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md outline-none" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                      <input required type="email" value={newUserEmail} onChange={(e) => setNewUserEmail(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md outline-none" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
                      <input required type="password" value={newUserPassword} onChange={(e) => setNewUserPassword(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md outline-none" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Role</label>
                      <select value={newUserRole} onChange={(e) => setNewUserRole(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md outline-none bg-white">
                        <option value="admin">Admin</option>
                        <option value="Editor">Editor</option>
                      </select>
                    </div>
                  </div>
                  {userMsg && <p className="text-sm text-indigo-600">{userMsg}</p>}
                  <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm font-medium transition-colors">
                    Create User
                  </button>
                </form>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <table className="w-full text-left text-sm">
                  <thead className="bg-slate-50 text-slate-600 font-medium border-b border-slate-200">
                    <tr>
                      <th className="px-6 py-4">Name</th>
                      <th className="px-6 py-4">Email</th>
                      <th className="px-6 py-4">Role</th>
                      <th className="px-6 py-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {loadingUsers ? (
                      <tr><td colSpan={4} className="text-center py-4">Loading...</td></tr>
                    ) : (
                      users.map(u => (
                        <tr key={u.uuid}>
                          <td className="px-6 py-4">{u.full_name}</td>
                          <td className="px-6 py-4">{u.email}</td>
                          <td className="px-6 py-4">
                            <span className={`px-2 py-1 rounded text-xs ${u.role === 'admin' ? 'bg-indigo-100 text-indigo-800' : 'bg-slate-100 text-slate-800'}`}>
                              {u.role}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <button onClick={() => handleDeleteUser(u.uuid)} className="text-red-500 hover:text-red-700 text-sm font-medium">Delete</button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'storage' && userRole === 'admin' && (
            <div className="space-y-6">
              <GoogleDriveAuth />

              <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                <h2 className="text-lg font-medium text-slate-900 mb-4">Add Drive Service Account</h2>
                <form className="space-y-4" onSubmit={handleAddDriveAccount}>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Account Email</label>
                      <input required type="email" value={newDriveEmail} onChange={(e) => setNewDriveEmail(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md outline-none" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Service Account Credentials (JSON)</label>
                      <input required type="text" value={newDriveJson} onChange={(e) => setNewDriveJson(e.target.value)} placeholder="Paste credentials JSON" className="w-full px-3 py-2 border border-slate-300 rounded-md outline-none" />
                    </div>
                  </div>
                  {driveMsg && <p className="text-sm text-indigo-600">{driveMsg}</p>}
                  <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm font-medium transition-colors">
                    Add Account
                  </button>
                </form>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <table className="w-full text-left text-sm">
                  <thead className="bg-slate-50 text-slate-600 font-medium border-b border-slate-200">
                    <tr>
                      <th className="px-6 py-4">Email</th>
                      <th className="px-6 py-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {loadingGdrive ? (
                      <tr><td colSpan={2} className="text-center py-4">Loading...</td></tr>
                    ) : gdriveAccounts.length === 0 ? (
                      <tr><td colSpan={2} className="text-center py-4 text-slate-400">No service accounts added yet.</td></tr>
                    ) : (
                      gdriveAccounts.map((acc: any) => (
                        <tr key={acc.id}>
                          <td className="px-6 py-4">{acc.email}</td>
                          <td className="px-6 py-4 text-right">
                            <button onClick={() => handleDeleteDriveAccount(acc.id)} className="text-red-500 hover:text-red-700 text-sm font-medium">Delete</button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'ai' && (
            <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm space-y-6">
              <div>
                <h2 className="text-lg font-medium text-slate-900 mb-1">AI API Keys</h2>
                <p className="text-sm text-slate-500 mb-4">Configure your API keys for automated translations and AI generation features.</p>
              </div>

              <form onSubmit={handleSaveAI} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Gemini API Keys (Comma-separated for multiple)</label>
                  <input type="text" placeholder="AIzaSy..., AIzaSy..." value={geminiKey} onChange={(e) => setGeminiKey(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm font-mono" />
                  <p className="text-xs text-slate-500 mt-1">Separate multiple keys with commas to automatically cycle through them if one fails or hits rate limits.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Groq API Key</label>
                  <input type="password" placeholder="gsk_..." value={groqKey} onChange={(e) => setGroqKey(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm font-mono" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">GitHub Models PAT</label>
                  <input type="password" placeholder="ghp_..." value={githubKey} onChange={(e) => setGithubKey(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm font-mono" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">DeepSeek API Key</label>
                  <input type="password" placeholder="sk-..." value={deepseekKey} onChange={(e) => setDeepseekKey(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm font-mono" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">NVIDIA NIM API Key</label>
                  <input type="password" placeholder="nvapi-..." value={nvidiaKey} onChange={(e) => setNvidiaKey(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm font-mono" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">YouTube Data API v3 Key</label>
                  <input type="password" placeholder="AIzaSy..." value={youtubeKey} onChange={(e) => setYoutubeKey(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm font-mono" />
                  <p className="text-xs text-slate-500 mt-1">Required for the bulk "Import from YouTube" feature to fetch video/playlist descriptions.</p>
                </div>
                
                <hr className="my-6 border-slate-200" />
                <h3 className="text-lg font-medium text-slate-900 mb-4">Video Player Integrations</h3>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">JW Player License Key</label>
                  <input type="password" placeholder="e.g. ABCDEFG..." value={jwplayerKey} onChange={(e) => setJwplayerKey(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm font-mono" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">JW Player Cloud Player URL</label>
                  <input type="text" placeholder="https://cdn.jwplayer.com/libraries/YOUR_PLAYER_ID.js" value={jwplayerUrl} onChange={(e) => setJwplayerUrl(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm font-mono" />
                  <p className="text-xs text-slate-500 mt-1">Leave empty to use native HTML5 video player. Provide a valid Cloud Player URL to enable JW Player in the frontend.</p>
                </div>
                
                {saveMessage && <p className="text-sm text-green-600">{saveMessage}</p>}
                
                <button type="submit" className="px-4 py-2 bg-slate-900 text-white rounded-md hover:bg-slate-800 text-sm font-medium transition-colors">
                  Save Changes
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
